/**
 * Edit Scheduled Class Dialog - Form for editing or deleting a scheduled class
 *
 * Pre-fills all fields with existing scheduled class data.
 * Allows updating room, teachers, day, and time.
 * Includes delete functionality with confirmation.
 */

import React, { useState, useEffect } from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  TextField,
  MenuItem,
  Box,
  Alert,
  CircularProgress,
  Typography,
  Divider,
  FormControl,
  InputLabel,
  Select,
  Chip,
  OutlinedInput,
  SelectChangeEvent,
  FormControlLabel,
  Switch,
} from '@mui/material';
import SaveIcon from '@mui/icons-material/Save';
import DeleteIcon from '@mui/icons-material/Delete';
import PushPinIcon from '@mui/icons-material/PushPin';
import SmartToyIcon from '@mui/icons-material/SmartToy';
import {
  useGetRoomsQuery,
  useGetTeachersQuery,
  useUpdateScheduledClassMutation,
  useDeleteScheduledClassMutation,
} from '../../../../../api/schedulingApi';
import type { ScheduledClass } from '../../../../../types/scheduling';

interface EditScheduledClassDialogProps {
  open: boolean;
  onClose: () => void;
  scheduledClass: ScheduledClass | null;
  studioId: string;
}

const EditScheduledClassDialog: React.FC<EditScheduledClassDialogProps> = ({
  open,
  onClose,
  scheduledClass,
  studioId,
}) => {
  const [selectedRoomId, setSelectedRoomId] = useState('');
  const [selectedTeacherIds, setSelectedTeacherIds] = useState<string[]>([]);
  const [day, setDay] = useState('');
  const [startTime, setStartTime] = useState('');
  const [endTime, setEndTime] = useState('');
  const [isManuallyScheduled, setIsManuallyScheduled] = useState(false);
  const [deleteConfirmOpen, setDeleteConfirmOpen] = useState(false);

  // Fetch data
  const { data: rooms = [], isLoading: roomsLoading } = useGetRoomsQuery({
    scheduleId: scheduledClass?.schedule || '',
    studioId,
  });
  const { data: teachers = [], isLoading: teachersLoading } =
    useGetTeachersQuery({
      scheduleId: scheduledClass?.schedule || '',
      studioId,
    });

  // Mutations
  const [updateScheduledClass, { isLoading: isUpdating, error: updateError }] =
    useUpdateScheduledClassMutation();
  const [deleteScheduledClass, { isLoading: isDeleting, error: deleteError }] =
    useDeleteScheduledClassMutation();

  // Initialize form when scheduledClass changes
  useEffect(() => {
    if (scheduledClass) {
      setSelectedRoomId(scheduledClass.room);
      setSelectedTeacherIds(scheduledClass.teachers || []);
      setDay(scheduledClass.day);
      // Extract HH:MM from time string (may include seconds)
      setStartTime(scheduledClass.start_time.substring(0, 5));
      setEndTime(scheduledClass.end_time.substring(0, 5));
      setIsManuallyScheduled(scheduledClass.is_manually_scheduled || false);
    }
  }, [scheduledClass]);

  const handleClose = () => {
    setDeleteConfirmOpen(false);
    onClose();
  };

  const handleTeacherChange = (event: SelectChangeEvent<string[]>) => {
    const { value } = event.target;
    setSelectedTeacherIds(typeof value === 'string' ? value.split(',') : value);
  };

  const handleUpdate = async () => {
    if (!scheduledClass || !selectedRoomId || !startTime || !endTime) {
      return;
    }

    try {
      await updateScheduledClass({
        id: scheduledClass.id,
        studioId,
        scheduleId: scheduledClass.schedule, // Pass scheduleId for cache invalidation
        data: {
          room: selectedRoomId,
          teachers: selectedTeacherIds,
          day,
          start_time: `${startTime}:00`, // Add seconds for Django TimeField format
          end_time: `${endTime}:00`, // Add seconds for Django TimeField format
          is_manually_scheduled: isManuallyScheduled,
        },
      }).unwrap();

      // Success - close dialog
      handleClose();
    } catch (err: any) {
      console.error('Failed to update scheduled class:', err);
      console.error('Error details:', JSON.stringify(err, null, 2));
      if (err.data) {
        console.error('Backend error response:', err.data);
      }
    }
  };

  const handleDelete = async () => {
    if (!scheduledClass) return;

    try {
      await deleteScheduledClass({
        id: scheduledClass.id,
        studioId,
        scheduleId: scheduledClass.schedule,
      }).unwrap();

      // Success - close both dialogs
      setDeleteConfirmOpen(false);
      handleClose();
    } catch (err) {
      console.error('Failed to delete scheduled class:', err);
    }
  };

  const isLoading = roomsLoading || teachersLoading;
  const canSubmit = selectedRoomId && !isUpdating && !isDeleting;

  if (!scheduledClass) {
    return null;
  }

  return (
    <>
      <Dialog open={open} onClose={handleClose} maxWidth="sm" fullWidth>
        <DialogTitle>Edit Scheduled Class</DialogTitle>
        <DialogContent>
          {isLoading && (
            <Box display="flex" justifyContent="center" p={2}>
              <CircularProgress />
            </Box>
          )}

          {!isLoading && (
            <Box
              sx={{ display: 'flex', flexDirection: 'column', gap: 2, pt: 1 }}
            >
              {updateError && (
                <Alert severity="error">
                  Failed to update class. Please try again.
                </Alert>
              )}

              {deleteError && (
                <Alert severity="error">
                  Failed to delete class. Please try again.
                </Alert>
              )}

              {/* Class Info (Read-only) */}
              <Box
                sx={{
                  p: 2,
                  bgcolor: 'action.hover',
                  borderRadius: 1,
                }}
              >
                <Typography variant="subtitle2" color="text.secondary">
                  Class
                </Typography>
                <Typography variant="body1" fontWeight="bold">
                  {scheduledClass.class_details.class_name}
                </Typography>
                <Typography variant="caption" color="text.secondary">
                  {scheduledClass.class_details.styles &&
                  scheduledClass.class_details.styles.length > 0
                    ? scheduledClass.class_details.styles
                        .map((s: { title: string }) => s.title)
                        .join(', ')
                    : 'No styles'}{' '}
                  - {scheduledClass.class_details.level}
                </Typography>
                <br />
                <Typography variant="caption" color="text.secondary">
                  Duration: {scheduledClass.class_details.duration} hours (
                  {Math.round(scheduledClass.class_details.duration * 60)}{' '}
                  minutes)
                </Typography>
              </Box>

              <Divider />

              {/* Scheduling Status Toggle */}
              <Box
                sx={{
                  p: 2,
                  border: 1,
                  borderColor: isManuallyScheduled ? 'primary.main' : 'divider',
                  borderRadius: 1,
                  bgcolor: isManuallyScheduled
                    ? 'primary.light'
                    : 'background.paper',
                }}
              >
                <FormControlLabel
                  control={
                    <Switch
                      checked={isManuallyScheduled}
                      onChange={e => setIsManuallyScheduled(e.target.checked)}
                      color="primary"
                    />
                  }
                  label={
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                      {isManuallyScheduled ? (
                        <PushPinIcon fontSize="small" color="primary" />
                      ) : (
                        <SmartToyIcon fontSize="small" color="action" />
                      )}
                      <Box>
                        <Typography variant="body2" fontWeight="bold">
                          {isManuallyScheduled
                            ? 'Manually Scheduled'
                            : 'Algorithm Generated'}
                        </Typography>
                        <Typography variant="caption" color="text.secondary">
                          {isManuallyScheduled
                            ? 'This class will be preserved when the scheduling algorithm re-runs'
                            : 'This class will be cleared and re-scheduled when the algorithm runs'}
                        </Typography>
                      </Box>
                    </Box>
                  }
                />
              </Box>

              {/* Editable Fields */}
              <TextField
                select
                label="Room"
                value={selectedRoomId}
                onChange={e => setSelectedRoomId(e.target.value)}
                required
                disabled={rooms.length === 0}
                helperText="Select the room for this class"
              >
                {rooms.map(room => (
                  <MenuItem key={room.id} value={room.id}>
                    {room.room_name}
                  </MenuItem>
                ))}
              </TextField>

              <FormControl fullWidth>
                <InputLabel>Teachers (Optional)</InputLabel>
                <Select
                  multiple
                  value={selectedTeacherIds}
                  onChange={handleTeacherChange}
                  input={<OutlinedInput label="Teachers (Optional)" />}
                  renderValue={selected => (
                    <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.5 }}>
                      {selected.map(value => {
                        const teacher = teachers.find(t => t.id === value);
                        return (
                          <Chip
                            key={value}
                            label={teacher?.teacher_name || value}
                            size="small"
                          />
                        );
                      })}
                    </Box>
                  )}
                >
                  {teachers.map(teacher => (
                    <MenuItem key={teacher.id} value={teacher.id}>
                      {teacher.teacher_name}
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>

              <TextField
                select
                label="Day"
                value={day}
                onChange={e => setDay(e.target.value)}
                required
              >
                {[
                  'Monday',
                  'Tuesday',
                  'Wednesday',
                  'Thursday',
                  'Friday',
                  'Saturday',
                  'Sunday',
                ].map(d => (
                  <MenuItem key={d} value={d}>
                    {d}
                  </MenuItem>
                ))}
              </TextField>

              <TextField
                type="time"
                label="Start Time"
                value={startTime}
                onChange={e => setStartTime(e.target.value)}
                required
                InputLabelProps={{ shrink: true }}
              />

              <TextField
                type="time"
                label="End Time"
                value={endTime}
                onChange={e => setEndTime(e.target.value)}
                required
                InputLabelProps={{ shrink: true }}
                helperText="Adjust the end time if needed for this specific instance"
              />
            </Box>
          )}
        </DialogContent>
        <DialogActions
          sx={{
            justifyContent: 'space-between',
            px: 3,
            pb: 2,
          }}
        >
          <Button
            onClick={() => setDeleteConfirmOpen(true)}
            color="error"
            startIcon={<DeleteIcon />}
            disabled={isDeleting || isUpdating}
          >
            Delete
          </Button>
          <Box>
            <Button onClick={handleClose} sx={{ mr: 1 }}>
              Cancel
            </Button>
            <Button
              onClick={handleUpdate}
              variant="contained"
              startIcon={<SaveIcon />}
              disabled={!canSubmit}
            >
              {isUpdating ? 'Saving...' : 'Save Changes'}
            </Button>
          </Box>
        </DialogActions>
      </Dialog>

      {/* Delete Confirmation Dialog */}
      <Dialog
        open={deleteConfirmOpen}
        onClose={() => setDeleteConfirmOpen(false)}
        maxWidth="xs"
      >
        <DialogTitle>Delete Scheduled Class?</DialogTitle>
        <DialogContent>
          <Typography>
            Are you sure you want to delete{' '}
            <strong>{scheduledClass.class_details.class_name}</strong> from{' '}
            <strong>{day}</strong> at <strong>{startTime}</strong>?
          </Typography>
          <Typography variant="body2" color="text.secondary" sx={{ mt: 1 }}>
            This action cannot be undone.
          </Typography>
        </DialogContent>
        <DialogActions>
          <Button
            onClick={() => setDeleteConfirmOpen(false)}
            disabled={isDeleting}
          >
            Cancel
          </Button>
          <Button
            onClick={handleDelete}
            color="error"
            variant="contained"
            disabled={isDeleting}
          >
            {isDeleting ? 'Deleting...' : 'Delete'}
          </Button>
        </DialogActions>
      </Dialog>
    </>
  );
};

export default EditScheduledClassDialog;
